/* eslint-disable no-unused-vars */
import { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react'
import {
  IWorkroomFolder,
  IWorkroomDocument,
  DocumentsAndFolderArray,
  IWorkroomItem,
  WorkroomItemType,
  IWorkroomUserPermissions,
} from 'Frontend/scripts/models/workrooms.model'
import { getFiles, getFolders, getMyWorkroomPermissions } from 'Frontend/scripts/services/workrooms.service'
import useQueryParams from 'Frontend/hooks/useQueryParams'

type IWoorkroomItem = IWorkroomDocument | IWorkroomFolder

interface WorkroomContextType {
  foldersAndDocuments: DocumentsAndFolderArray
  currentFolder: string
  isLoading: boolean
  selectedFiles: IWorkroomItem[]
  currentWorkroomId: string | null
  errorMessage: string | null
  permissions: IWorkroomUserPermissions | null
  addItem: (item: IWorkroomDocument | IWorkroomFolder) => void
  removeItem: (id: number) => void
  setCurrentWorkroomId: (value: string | null) => void
  setErrorMessage: (value: string) => void
  resetSelectedFiles: () => void
  setFoldersAndDocuments: (value: DocumentsAndFolderArray) => void
  setCurrentActiveFolder: (value: string) => void
  setIsLoading: (value: boolean) => void
  fetchFilesAndFolders: () => void
}

const WorkroomContext = createContext<WorkroomContextType | undefined>(undefined)

export const WorkroomProvider = ({ children }: { children: ReactNode }) => {
  const { getQueryParam, setQueryParam, removeQueryParam } = useQueryParams()
  const [selectedFiles, setSelectedFiles] = useState<IWorkroomItem[]>([])
  const [currentFolder, setCurrentFolder] = useState<string>(getQueryParam('folderId') ?? 'root')
  const [currentWorkroomId, setCurrentWorkroomId] = useState<string | null>(getQueryParam('id'))
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [foldersAndDocuments, setFoldersAndDocuments] = useState<DocumentsAndFolderArray>([])
  const [permissions, setPermissions] = useState<IWorkroomUserPermissions | null>(null)

  const addItem = (item: IWoorkroomItem) => {
    setSelectedFiles((prev) => [...prev, item])
  }

  const removeItem = (id: number) => {
    setSelectedFiles((prev) => prev.filter((item) => item.id !== id))
  }

  const resetSelectedFiles = () => {
    setSelectedFiles([])
  }

  const setCurrentActiveFolder = (folderId: string) => {
    setCurrentFolder(folderId)
    if (folderId === 'root') {
      removeQueryParam('folderId')
    } else {
      setQueryParam('folderId', folderId)
    }
  }

  const fetchUserRights = async () => {
    getMyWorkroomPermissions(parseInt(currentWorkroomId as string)).then((permissions) => {
      setPermissions(permissions)
    })
  }

  const fetchFilesAndFolders = useCallback(async () => {
    if (!currentWorkroomId || !currentFolder) return

    try {
      setIsLoading(true)
      resetSelectedFiles()
      const [filesResponse, folderResponse] = await Promise.all([
        getFiles(currentWorkroomId, currentFolder),
        getFolders(currentWorkroomId, currentFolder),
      ])

      let files = filesResponse.map((file: IWorkroomDocument) => {
        return { ...file, type: WorkroomItemType.FILE }
      })
      let folders = folderResponse.map((folder: IWorkroomFolder) => {
        return { ...folder, type: WorkroomItemType.FOLDER }
      })

      const currentWorkroomItems = [...folders, ...files].sort((a, b) => {
        if (a.type > b.type) return -1
        if (a.type < b.type) return 1

        return a.name.localeCompare(b.name)
      })

      setFoldersAndDocuments(currentWorkroomItems)
    } catch {
      console.error('Failed to load news categories')
    } finally {
      setIsLoading(false)
    }
  }, [currentWorkroomId, currentFolder])

  useEffect(() => {
    fetchFilesAndFolders()
  }, [currentWorkroomId, currentFolder])

  useEffect(() => {
    if (currentWorkroomId) fetchUserRights()
  }, [currentWorkroomId])

  return (
    <WorkroomContext.Provider
      value={{
        foldersAndDocuments,
        currentFolder,
        isLoading,
        selectedFiles,
        currentWorkroomId,
        errorMessage,
        permissions,
        addItem,
        removeItem,
        setErrorMessage,
        setCurrentWorkroomId,
        resetSelectedFiles,
        setFoldersAndDocuments,
        setIsLoading,
        setCurrentActiveFolder,
        fetchFilesAndFolders,
      }}
    >
      {children}
    </WorkroomContext.Provider>
  )
}

export const useWorkroom = () => {
  const context = useContext(WorkroomContext)
  if (!context) {
    throw new Error('useWorkroom must be used within a WorkroomProvider')
  }
  return context
}
